import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { queryOptions, keepPreviousData } from '@tanstack/vue-query';
import type { QueryOptions } from '@tanstack/vue-query';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { Article9GroupBy, CountryDashboard, GlobalDashboard } from '@sysarb/sysarb-types-v2/services/v1/base-registry/globalDashboard';
import type { CountryCode, EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums';
import type StatisticalCode from '@sysarb/sysarb-api/models/entities/StatisticalCode';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';

const baseRegistryService = new RestManager(`https://${getApiHost()}/base-registry-service`);

export type AllowedLegalEntitiesResult = {
    showAll: boolean;
    legalEntities: string[];
};

type GetGlobalDashboardParams = {
    legalEntity?: string | null;
    salaryType?: EmploymentSalaryType | null;
};

type GetCountryDashboardParams = {
    countryCode: CountryCode | null | undefined;
    legalEntity?: string | null;
    salaryType?: EmploymentSalaryType | null;
    article9GroupBy?: Article9GroupBy;
};

type GetAllowedLegalEntitiesForCountryParams = {
    countryCode?: CountryCode | null;
};

type GetValidSalaryTypesForCountryParams = {
    countryCode: CountryCode | null | undefined;
    legalEntity?: string | null;
};

type BaseRegistryOptions = {
    queries: {
        hasDataImported: (enabled?: boolean) => QueryOptions<boolean, Error>,
        getGlobalDashboard: (params?: GetGlobalDashboardParams, enabled?: boolean) => QueryOptions<GlobalDashboard, Error>,
        getAllowedLegalEntities: (enabled?: boolean) => QueryOptions<AllowedLegalEntitiesResult, Error>,
        getCountryDashboard: (params: GetCountryDashboardParams, enabled?: boolean) => QueryOptions<CountryDashboard, Error>,
        getAllowedLegalEntitiesForCountry: (params: GetAllowedLegalEntitiesForCountryParams, enabled?: boolean) => QueryOptions<string[], Error>,
        getEmploymentSalaryTypes: (employmentId: string | null, enabled?: boolean) => QueryOptions<EmploymentSalaryType[] | null, Error>,
        getStatisticalCodes: (enabled?: boolean) => QueryOptions<StatisticalCode[], Error>,
        getValidSalaryTypesForCountry: (params: GetValidSalaryTypesForCountryParams, enabled?: boolean) => QueryOptions<EmploymentSalaryType[], Error>,
    },
};

export const baseRegistry: BaseRegistryOptions = {
    queries: {
        hasDataImported: (enabled: boolean = false): QueryOptions<boolean, Error> => {
            const { instance } = useLoginStore();
            const url = `v1/${instance}/base-register/has-data`;
            return queryOptions<boolean, Error>({
                queryKey: ['has-data-imported', instance, url],
                queryFn: async (): Promise<boolean> => {
                    try {
                        const result = await baseRegistryService.post<{ hasData: boolean }>({}, url);
                        return result.hasData;
                    } catch (error) {
                        console.error('Error checking if data is imported', error);
                        throw new Error('Failed to check if data is imported');
                    }
                },
                enabled: () => enabled
            });
        },
        getGlobalDashboard: (params?: GetGlobalDashboardParams, enabled: boolean = false): QueryOptions<GlobalDashboard, Error> => {
            const { instanceTopic } = useLoginStore();
            return queryOptions<GlobalDashboard, Error>({
                queryKey: ['global-dashboard', instanceTopic, params?.legalEntity ?? null, params?.salaryType ?? null],
                queryFn: async (): Promise<GlobalDashboard> => {
                    const searchParams = new URLSearchParams();
                    if (params?.legalEntity != null && params.legalEntity !== '') searchParams.set('legalEntity', params.legalEntity);
                    if (params?.salaryType != null ) searchParams.set('salaryType', params.salaryType.toString());
                    const query = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
                    try {
                        return await baseRegistryService.get<GlobalDashboard>(`v2/${instanceTopic}/global-dashboard${query}`);
                    } catch (error) {
                        console.error('Error fetching global dashboard', error);
                        throw new Error('Failed to fetch global dashboard');
                    }
                },
                enabled: () => enabled,
                placeholderData: keepPreviousData,
            });
        },
        getAllowedLegalEntities: (enabled: boolean = false): QueryOptions<AllowedLegalEntitiesResult, Error> => {
            const { instanceTopic } = useLoginStore();
            return queryOptions<AllowedLegalEntitiesResult, Error>({
                queryKey: ['allowed-legal-entities', instanceTopic],
                queryFn: async (): Promise<AllowedLegalEntitiesResult> => {
                    try {
                        return await baseRegistryService.get<AllowedLegalEntitiesResult>(`v1/${instanceTopic}/global-dashboard/legal-entities`);
                    } catch (error) {
                        console.error('Error fetching allowed legal entities', error);
                        throw new Error('Failed to fetch allowed legal entities');
                    }
                },
                enabled: () => enabled
            });
        },
        getCountryDashboard: (params: GetCountryDashboardParams, enabled: boolean = false): QueryOptions<CountryDashboard, Error> => {
            const { instanceTopic } = useLoginStore();
            return queryOptions<CountryDashboard, Error>({
                queryKey: ['country-dashboard', instanceTopic, params.countryCode ?? null, params.legalEntity ?? null, params.salaryType ?? null, params.article9GroupBy ?? null],
                queryFn: async (): Promise<CountryDashboard> => {
                    if (params.countryCode == null) {
                        console.error('getCountryDashboard called without a countryCode');
                        throw new Error('countryCode is required');
                    }
                    const searchParams = new URLSearchParams();
                    searchParams.set('country', params.countryCode);
                    if (params.legalEntity != null && params.legalEntity !== '') searchParams.set('legalEntity', params.legalEntity);
                    if (params.salaryType != null) searchParams.set('salaryType', params.salaryType.toString());
                    if (params.article9GroupBy != null) searchParams.set('article9GroupBy', params.article9GroupBy);
                    try {
                        return await baseRegistryService.get<CountryDashboard>(`v2/${instanceTopic}/country-dashboard?${searchParams.toString()}`);
                    } catch (error) {
                        console.error('Error fetching country dashboard', error);
                        throw new Error('Failed to fetch country dashboard');
                    }
                },
                enabled: () => enabled && params.countryCode != null,
                placeholderData: keepPreviousData,
            });
        },
        getAllowedLegalEntitiesForCountry: (params: GetAllowedLegalEntitiesForCountryParams, enabled: boolean = false): QueryOptions<string[], Error> => {
            const { instanceTopic } = useLoginStore();
            return queryOptions<string[], Error>({
                queryKey: ['allowed-legal-entities-country', instanceTopic, params.countryCode],
                queryFn: async (): Promise<string[]> => {
                    try {
                        return await baseRegistryService.get<string[]>(`v1/${instanceTopic}/country-dashboard/legal-entities?country=${params.countryCode}`);
                    } catch (error) {
                        console.error('Error fetching allowed legal entities for country', error);
                        throw new Error('Failed to fetch allowed legal entities for country');
                    }
                },
                enabled: () => enabled && params.countryCode != null
            });
        },
        getEmploymentSalaryTypes: (employmentId: string | null, enabled: boolean = true): QueryOptions<EmploymentSalaryType[] | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.base-register.employment.${employmentId}.involve-salary-types`;
            return queryOptions<EmploymentSalaryType[] | null, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['get-employment-salary-types', instanceTopic, employmentId, topic],
                queryFn: async (): Promise<EmploymentSalaryType[] | null> => {
                    if (employmentId == null) return null;
                    const resgate = ResgateManager.getInstance();
                    const result = await resgate.collection<EmploymentSalaryType>(topic);
                    if (result instanceof ResErr) throw new Error(result.message);
                    return result.list ?? null;
                },
                enabled: () => enabled && employmentId != null,
            });
        },
        /**
         * The instance's statistical codes.
         * @param {boolean} [enabled] - Whether to run the query.
         * @returns {QueryOptions<StatisticalCode[], Error>} Query options.
         */
        getStatisticalCodes: (enabled: boolean = true): QueryOptions<StatisticalCode[], Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.statisticalcodes`;
            return queryOptions<StatisticalCode[], Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['statisticalCodes', instanceTopic, topic],
                queryFn: async (): Promise<StatisticalCode[]> => {
                    const result = await ResgateManager.getInstance().collection<StatisticalCode>(topic);
                    if (result instanceof ResErr) throw new Error(result.message);
                    return result.list;
                },
                enabled: () => enabled,
            });
        },
        getValidSalaryTypesForCountry: (params: GetValidSalaryTypesForCountryParams, enabled: boolean = false): QueryOptions<EmploymentSalaryType[], Error> => {
            const { instanceTopic } = useLoginStore();
            return queryOptions<EmploymentSalaryType[], Error>({
                queryKey: ['valid-salary-types-country', instanceTopic, params.countryCode ?? null, params.legalEntity ?? null],
                queryFn: async (): Promise<EmploymentSalaryType[]> => {
                    if (params.countryCode == null) throw new Error('countryCode is required');
                    const searchParams = new URLSearchParams();
                    searchParams.set('country', params.countryCode);
                    if (params.legalEntity != null && params.legalEntity !== '') searchParams.set('legalEntity', params.legalEntity);
                    try {
                        return await baseRegistryService.get<EmploymentSalaryType[]>(`v1/${instanceTopic}/country-dashboard/valid-salary-types?${searchParams.toString()}`);
                    } catch (error) {
                        console.error('Error fetching valid salary types for country', error);
                        throw new Error('Failed to fetch valid salary types for country');
                    }
                },
                enabled: () => enabled && params.countryCode != null,
                placeholderData: keepPreviousData,
            });
        }
    }
};
